/**
 * Artwork validation (spec 018 / Addendum §0.3, §6). Reject oversize/unsupported/corrupt
 * artwork CLIENT-SIDE, before it enters the render queue — so no mockup-generation cost is
 * consumed on a file that would fail. Pixels never leave the device for this check.
 *
 * The default ceiling is 2500 px (the addendum's number); a super-admin can tune it via the
 * `max_artwork_px` entitlement and pass it as `maxPx`. The exact value is VALIDATE-WITH-ANAND.
 */
export const DEFAULT_MAX_ARTWORK_PX = 2500;
// Common raster image types the engine's decoder handles (image crate default
// features). `.jpg` AND `.jpeg`, plus gif/bmp/tiff so folder import accepts any of
// the usual design exports, not just PNG/JPEG.
export const ALLOWED_ARTWORK_FORMATS = ["png", "jpg", "jpeg", "webp", "gif", "bmp", "tiff", "tif"] as const;

export type ArtworkRejectCode = "too_large" | "unsupported_format" | "corrupt";

export type ArtworkValidation =
  | { ok: true; warning?: string }
  | { ok: false; code: ArtworkRejectCode; message: string };

/** Lower-cased file extension (no dot). */
export function artworkFormat(name: string): string {
  return (name.toLowerCase().split(".").pop() ?? "").trim();
}

function formatAllowed(name: string): boolean {
  return (ALLOWED_ARTWORK_FORMATS as readonly string[]).includes(artworkFormat(name));
}

/** Format-only check (used where the decoder is unavailable). */
export function validateArtworkFormat(name: string): ArtworkValidation {
  if (!formatAllowed(name)) {
    return {
      ok: false,
      code: "unsupported_format",
      message: `Unsupported format ".${artworkFormat(name)}". Allowed: PNG, JPG, JPEG, WebP.`,
    };
  }
  return { ok: true };
}

/** Validate a known size + filename. Pure — the unit-testable core. */
export function validateArtworkDims(input: {
  width: number;
  height: number;
  name: string;
  maxPx?: number;
}): ArtworkValidation {
  const max = input.maxPx ?? DEFAULT_MAX_ARTWORK_PX;
  if (!formatAllowed(input.name)) {
    return {
      ok: false,
      code: "unsupported_format",
      message: `Unsupported format ".${artworkFormat(input.name)}". Allowed: PNG, JPG, JPEG, WebP.`,
    };
  }
  if (input.width > max || input.height > max) {
    return {
      ok: false,
      code: "too_large",
      message:
        `Artwork rejected. Uploaded ${input.width}×${input.height} px. ` +
        `Maximum allowed ${max}×${max} px. Please resize the file and upload again.`,
    };
  }
  return { ok: true };
}

/** Decode a file to learn its dimensions, then validate. A decode failure ⇒ corrupt.
 *  Files without bytes (path-only refs) can't be inspected here and are allowed through. */
export async function inspectArtwork(
  io: { decodeImage: (b: Uint8Array) => Promise<{ width: number; height: number }> },
  file: { name: string; bytes?: Uint8Array | null },
  maxPx?: number,
): Promise<ArtworkValidation> {
  if (!formatAllowed(file.name)) {
    return {
      ok: false,
      code: "unsupported_format",
      message: `Unsupported format ".${artworkFormat(file.name)}". Allowed: PNG, JPG, JPEG, WebP.`,
    };
  }
  if (!file.bytes || file.bytes.length === 0) return { ok: true };
  let dims: { width: number; height: number };
  try {
    dims = await io.decodeImage(file.bytes);
  } catch {
    return {
      ok: false,
      code: "corrupt",
      message: `"${file.name}" could not be read (corrupt or unsupported image).`,
    };
  }
  return validateArtworkDims({ width: dims.width, height: dims.height, name: file.name, maxPx });
}
